import React, { useRef, useEffect, useCallback, useState } from 'react';
import { useBottomPanel } from '../../contexts/BottomPanelContext';
import '../../style/bottom-panel.css';

const MIN_H     = 140;
const HANDLE_H  = 28;
const MAX_H_RATIO = 0.62;
const DRAG_THRESHOLD = 4; // px 이상 움직이면 드래그로 판정

function BottomPanel() {
  const {
    isOpen, setOpen,
    panelHeight, setPanelHeight,
    content, activeTab, setActiveTab,
  } = useBottomPanel();

  const isDraggingRef  = useRef(false);
  const hasDraggedRef  = useRef(false);
  const dragStartY     = useRef(0);
  const dragStartH     = useRef(0);
  const [localH, setLocalH]         = useState(panelHeight);
  const [dragging, setDragging]     = useState(false); // transition 끄기용

  // CSS 변수로 메인 콘텐츠 패딩 자동 보정
  useEffect(() => {
    const h = isOpen ? localH + HANDLE_H : HANDLE_H;
    document.documentElement.style.setProperty('--bottom-panel-open-height', `${h}px`);
  }, [isOpen, localH]);

  // panelHeight 외부 변경 반영
  useEffect(() => { setLocalH(panelHeight); }, [panelHeight]);

  // ── 드래그 시작 ──
  const handlePointerDown = useCallback((e) => {
    if (e.button !== undefined && e.button !== 0) return;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    isDraggingRef.current  = true;
    hasDraggedRef.current  = false;
    dragStartY.current     = clientY;
    dragStartH.current     = localH;
    setDragging(true);
    document.body.style.userSelect = 'none';
    document.body.style.cursor     = 'ns-resize';
    document.documentElement.classList.add('bottom-panel-dragging');
    e.preventDefault();
  }, [localH]);

  // ── 드래그 이동/종료 ──
  useEffect(() => {
    const onMove = (e) => {
      if (!isDraggingRef.current) return;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const delta   = dragStartY.current - clientY; // ↑ 양수

      if (Math.abs(delta) > DRAG_THRESHOLD) hasDraggedRef.current = true;

      const maxH = Math.floor(window.innerHeight * MAX_H_RATIO);
      const newH = Math.max(MIN_H, Math.min(maxH, dragStartH.current + delta));
      setLocalH(newH);

      // 위로 충분히 끌면 자동 오픈
      if (delta > 20 && !isOpen) setOpen(true);
    };

    const onEnd = () => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      setDragging(false);
      document.body.style.userSelect = '';
      document.body.style.cursor     = '';
      document.documentElement.classList.remove('bottom-panel-dragging');

      if (!hasDraggedRef.current) {
        // 클릭 판정 → 토글
        setOpen((prev) => !prev);
      } else {
        // 드래그 종료 → 높이 저장
        setPanelHeight(localH);
      }
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup',   onEnd);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend',  onEnd);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup',   onEnd);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend',  onEnd);
    };
  }, [localH, isOpen, setOpen, setPanelHeight]);

  const hasContent    = !!content;
  const tabs          = content?.tabs ?? [];
  const headerActions = Array.isArray(content?.actions) ? content.actions.filter(Boolean) : [];
  const activeTabEntry = tabs.find((t) => t.id === activeTab) ?? tabs[0] ?? null;
  const activeTabComp  = activeTabEntry?.component ?? null;
  const noPadding      = !!activeTabEntry?.noPadding;
  const isSingleTab    = tabs.length <= 1;
  const showHeader     = tabs.length > 1 || headerActions.length > 0;

  // 콘텐츠 없으면 렌더 안 함
  if (!hasContent) return null;

  const showFullHeight = isOpen || dragging;

  return (
    <div
      className={`bottom-panel-outer${isOpen ? ' open' : ''}${dragging ? ' dragging' : ''}`}
      style={{ height: showFullHeight ? localH + HANDLE_H : HANDLE_H }}
    >
      {/* ── 핸들 — 패널에 딱 붙음 ── */}
      <div
        className={`bottom-panel-handle${isOpen ? ' open' : ''}`}
        onMouseDown={handlePointerDown}
        onTouchStart={handlePointerDown}
        aria-label={isOpen ? '드래그하여 높이 조절 / 클릭하여 접기' : '클릭하여 펼치기'}
        title={isOpen ? '드래그: 크기 조절  |  클릭: 접기' : '클릭하여 펼치기'}
      >
        <div className="bottom-panel-handle-inner">
          <div className="bottom-panel-handle-bar" />
          <span className="bottom-panel-handle-label">
            {isOpen
              ? '드래그하여 높이 조절'
              : (activeTabEntry?.label ?? '프로젝트 · 플랜')}
          </span>
        </div>
      </div>

      {/* ── 패널 콘텐츠 — 헤더(탭·액션) + 바디 ── */}
      <div className="bottom-panel-content">
        {showHeader && (
          <div className="bottom-panel-header">
            {isSingleTab ? (
              <span className="bottom-panel-single-title">
                {activeTabEntry?.label ?? ''}
              </span>
            ) : (
              tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`bottom-panel-tab${activeTab === tab.id ? ' active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))
            )}
            {headerActions.length > 0 && (
              <div className="bottom-panel-header-right">
                {headerActions.map((action, index) => (
                  <button
                    key={`${action.label}-${index}`}
                    type="button"
                    className={`bottom-panel-action${action.primary ? ' primary' : ''}`}
                    onClick={action.onClick}
                    title={action.label}
                    aria-label={action.label}
                  >
                    {action.icon && (
                      <span className="material-symbols-rounded">{action.icon}</span>
                    )}
                    <span>{action.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        <div className={`bottom-panel-body${noPadding ? ' no-padding' : ''}`}>
          {activeTabComp}
        </div>
      </div>
    </div>
  );
}

export default BottomPanel;
